import { Injectable, computed, inject, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';

/**
 * État de chargement global de l'application.
 *
 * Compteur de références (ref-count) : chaque opération asynchrone appelle
 * `start()` à son début et `stop()` à sa fin. `active` reste vrai tant qu'au
 * moins une opération est en cours — ce qui permet d'empiler plusieurs
 * requêtes HTTP et une transition de route sans clignotement.
 *
 * Alimenté automatiquement par :
 *  - `loadingInterceptor` (requêtes HTTP, sauf opt-out via SKIP_LOADING) ;
 *  - le suivi des événements du Router (transitions de pages), câblé ici.
 *
 * Pour un usage manuel (traitement long non HTTP) : `start()` / `stop()` ou
 * l'assistant `track(promise)`.
 */
@Injectable({ providedIn: 'root' })
export class LoadingService {
  private readonly router = inject(Router);

  /** Nombre d'opérations en cours ; jamais négatif. */
  private readonly count = signal(0);

  /** Vrai dès qu'une opération est en cours. */
  readonly active = computed(() => this.count() > 0);

  constructor() {
    // Suivi des transitions de route : une navigation = une opération.
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.start();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.stop();
      }
    });
  }

  /** Signale le début d'une opération. */
  start(): void {
    this.count.update((n) => n + 1);
  }

  /** Signale la fin d'une opération (borné à 0 pour rester robuste). */
  stop(): void {
    this.count.update((n) => (n > 0 ? n - 1 : 0));
  }

  /**
   * Encadre une promesse : affiche le loading pendant son exécution, quel
   * qu'en soit le dénouement. Pratique pour un traitement long non HTTP.
   */
  async track<T>(work: Promise<T>): Promise<T> {
    this.start();
    try {
      return await work;
    } finally {
      this.stop();
    }
  }
}
